import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef } from 'react';

import {
  getOccupiedViewportPx,
  getRemainingViewportHeight,
} from '../../../util/remainingViewport';

const SuggestionsContainer = ({
  containerProps,
  children,
  onOpenChange,
  constrainToViewport = false,
}) => {
  // Autosuggest renders null children when its list is closed, including Escape.
  const open = children !== null;
  const containerRef = containerProps.ref;
  const nodeRef = useRef(null);

  const syncMaxHeight = useCallback(() => {
    const node = nodeRef.current;
    if (!node) {
      return;
    }

    // Desktop keeps theme/CSS max-height (CommonNavigation width + Autosuggest
    // calc). Measured remaining-viewport height is for mobile scroll ownership.
    if (!open || !constrainToViewport) {
      node.style.maxHeight = '';
      return;
    }

    const top = node.getBoundingClientRect().top;
    const visualViewportOffsetTop =
      typeof window !== 'undefined' &&
      window.visualViewport &&
      typeof window.visualViewport.offsetTop === 'number'
        ? window.visualViewport.offsetTop
        : 0;
    const occupied = getOccupiedViewportPx(top, visualViewportOffsetTop);
    node.style.maxHeight = `${getRemainingViewportHeight(occupied)}px`;
  }, [constrainToViewport, open]);

  const setRef = useCallback(
    (node) => {
      nodeRef.current = node;
      // The library uses this DOM ref for keyboard scrolling and click handling.
      containerRef(node);
      if (node) {
        // Report during commit, without updating QuickSearch while rendering.
        onOpenChange(open);
      }
    },
    [containerRef, onOpenChange, open]
  );

  // useEffect (not useLayoutEffect): this tree is SSR'd with QuickSearch and
  // useLayoutEffect warns under renderToString even when window exists (jsdom).
  // Suggestions open only after client interaction; CSS max-height covers the
  // brief frame before the measured height lands.
  useEffect(() => {
    syncMaxHeight();

    if (!open || !constrainToViewport || typeof window === 'undefined') {
      return undefined;
    }

    window.addEventListener('resize', syncMaxHeight);
    const visualViewport = window.visualViewport;
    if (visualViewport) {
      visualViewport.addEventListener('resize', syncMaxHeight);
      visualViewport.addEventListener('scroll', syncMaxHeight);
    }

    return () => {
      window.removeEventListener('resize', syncMaxHeight);
      if (visualViewport) {
        visualViewport.removeEventListener('resize', syncMaxHeight);
        visualViewport.removeEventListener('scroll', syncMaxHeight);
      }
    };
  }, [constrainToViewport, open, syncMaxHeight]);

  const handleScroll = useCallback(() => {
    if (!open) {
      return;
    }
    syncMaxHeight();
  }, [open, syncMaxHeight]);

  const { className, ...restContainerProps } = containerProps;

  return (
    <div
      {...restContainerProps}
      className={classNames(className, {
        'QuickSearch__suggestions--viewport-scroll': open,
      })}
      onScroll={handleScroll}
      ref={setRef}
    >
      {children}
    </div>
  );
};

SuggestionsContainer.propTypes = {
  containerProps: PropTypes.shape({ ref: PropTypes.func.isRequired })
    .isRequired,
  children: PropTypes.node,
  onOpenChange: PropTypes.func.isRequired,
  constrainToViewport: PropTypes.bool,
};

export default SuggestionsContainer;
